import { useEffect, useRef, useState, useId } from 'react';
import PropTypes from 'prop-types';
import './TubeTooltip.css';

/**
 * Wrap ANY element/component as the trigger via children.
 * - trigger="hover" uses pure CSS :hover to show the tooltip.
 * - trigger="click" toggles with click (and adds a close button).
 * - contentHtml (string) OR content (ReactNode).
 */
function TubeTooltip(props) {
    const {
        children,
        trigger = 'hover',              // 'hover' | 'click'
        position = 'top',               // 'top' | 'right' | 'bottom' | 'left'
        content,
        contentHtml,
        sx,
        className
    } = props;

    const isClick = trigger === 'click';
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const tooltipId = useId();

    // Close on outside click
    useEffect(() => {
        if (!isClick || !open) return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isClick, open]);

    const toggle = () => setOpen(v => !v);

    const wrapperProps = isClick
        ? {
            onClick: toggle,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle();
                }
            },
            tabIndex: 0,
            'aria-haspopup': 'dialog',
            'aria-expanded': open ? 'true' : 'false',
            'aria-describedby': open ? tooltipId : undefined
        }
        : {
            'aria-describedby': tooltipId
        };

    return (
        <span
            ref={wrapRef}
            style={sx}
            className={`tube-tooltip ${position} ${isClick && open ? 'open' : ''} ${className ? className : ''}`}
            {...wrapperProps}
        >
            {/* Trigger is whatever you pass as children */}
            {children}

            {/* Tooltip bubble */}
            <span id={tooltipId} className={`tube-tooltip-content ${isClick ? (open ? 'visible' : '') : ''}`} role="tooltip">
                {contentHtml
                    ? <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                    : content}
                {isClick ? (
                    <button
                        type="button"
                        className="tube-tooltip-close"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                        }}
                        aria-label="Close tooltip"
                    >
                        ×
                    </button>
                ) : null}
                <span className="tube-tooltip-arrow" />
            </span>
        </span>
    );
}

export default TubeTooltip;

TubeTooltip.propTypes = {
    children: PropTypes.node.isRequired,         // trigger (any component/element)
    trigger: PropTypes.oneOf(['hover', 'click']),
    position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    content: PropTypes.any,                      // ReactNode
    contentHtml: PropTypes.string,               // HTML string
    sx: PropTypes.object,
    className: PropTypes.string
};
